import { useEffect, useMemo, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import api from "../../../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../../components/ui/command";

const cache = {};
export function usePersonnel(company, positions) {
  const key = `${company || ""}|${(positions || []).join(",")}`;
  const [list, setList] = useState(cache[key] || []);
  useEffect(() => {
    if (cache[key]) { setList(cache[key]); return; }
    api.get(`/ops/personnel/options?company=${company || ""}&position=${encodeURIComponent((positions || []).join(","))}`).then((r) => { cache[key] = r.data; setList(r.data); }).catch(() => setList([]));
  }, [key]); // eslint-disable-line
  return list;
}
export function clearPersonnelCache() { Object.keys(cache).forEach((k) => delete cache[k]); }

const SHORT = { "Business Contact (BC)": "BC", "Lead Driver": "Lead", "Operations Manager": "Ops Mgr", "Owner / Authorized Officer": "Owner", "HR Representative": "HR", "Administrative Support": "Admin", Driver: "Driver", Other: "Other" };

/* Searchable person picker fed by the Master Personnel Directory.
   value: display name string (or array when multiple). allowFreeText lets external names (e.g. FedEx reps) be typed. */
export function PersonPicker({ value, onChange, company, positions, multiple = false, allowFreeText = false, placeholder = "Search name…", testid, disabled, className = "" }) {
  const people = usePersonnel(company, positions);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = useMemo(() => (multiple ? (Array.isArray(value) ? value : []) : value ? [value] : []), [value, multiple]);
  const pick = (name, person) => {
    if (multiple) { const next = selected.includes(name) ? selected.filter((x) => x !== name) : [...selected, name]; onChange(next, person); }
    else { onChange(name === value ? "" : name, person); setOpen(false); }
    setQ("");
  };
  const filtered = people.filter((p) => !q || `${p.display_name} ${p.full_name} ${p.email}`.toLowerCase().includes(q.toLowerCase()));
  const exact = people.some((p) => p.display_name.toLowerCase() === q.trim().toLowerCase());
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} data-testid={testid} className={`w-full min-h-10 flex items-center gap-1 flex-wrap rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-left text-sm text-white disabled:opacity-60 ${className}`}>
          {selected.length === 0 ? <span className="text-slate-500 flex-1">{placeholder}</span> : multiple ? selected.map((s) => <span key={s} className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-2 py-0.5 text-xs">{s}{!disabled && <X className="w-3 h-3 text-slate-400" onClick={(e) => { e.stopPropagation(); pick(s); }} />}</span>) : <span className="flex-1 truncate">{selected[0]}</span>}
          <ChevronsUpDown className="w-4 h-4 text-slate-500 ml-auto shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[260px] bg-slate-900 border-slate-700 text-white" align="start">
        <Command shouldFilter={false}>
          <CommandInput data-testid={testid ? `${testid}-search` : undefined} value={q} onValueChange={setQ} placeholder="Type to search…" className="text-white" />
          <CommandList className="max-h-64">
            <CommandEmpty className="p-3 text-xs text-slate-400">{allowFreeText ? "No directory match — press Enter or the option below to use the typed name." : "No active personnel match. Add them in the Personnel Directory."}</CommandEmpty>
            <CommandGroup>
              {allowFreeText && q.trim() && !exact && <CommandItem value={`__free_${q}`} onSelect={() => pick(q.trim(), null)} data-testid={testid ? `${testid}-free` : undefined} className="text-amber-300 aria-selected:bg-slate-800">Use "{q.trim()}" (not in directory)</CommandItem>}
              {filtered.map((p) => (
                <CommandItem key={p.id} value={p.id} onSelect={() => pick(p.display_name, p)} data-testid={testid ? `${testid}-opt` : undefined} className="aria-selected:bg-slate-800 text-white cursor-pointer">
                  <Check className={`w-4 h-4 mr-2 ${selected.includes(p.display_name) ? "opacity-100 text-amber-400" : "opacity-0"}`} />
                  <span className="flex-1">{p.display_name}</span>
                  <span className="text-[10px] text-slate-500 ml-2">{p.positions.map((x) => SHORT[x] || x).join(" · ")}{p.companies.length > 1 ? " · both cos." : ""}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
